// PostHog product-analytics client.
//
// Bootstraps the posthog-js SDK with autocapture (every click + pageview
// fires automatically) and session recording. Returns the configured
// instance, or `null` if env vars are missing — App.js falls back to
// rendering without the PostHogProvider so the app still works offline
// / in environments that don't have PostHog credentials.
//
// Setup:
//   1. Sign up at https://us.posthog.com (free tier — 1M events/mo).
//   2. Create a project, copy the Project API Key (starts with `phc_`).
//   3. Add to frontend/.env (and production deploy env):
//        REACT_APP_POSTHOG_KEY=phc_xxxxxxxxxxxx
//        REACT_APP_POSTHOG_HOST=https://us.i.posthog.com
//        REACT_APP_POSTHOG_DASHBOARD_URL=https://us.posthog.com/project/{project_id}/home
//   4. In PostHog project settings, turn ON "Record user sessions".
//   5. Restart the dev server / redeploy.
//
// Coexistence: this is purely additive. The existing /api/analytics/track
// pipeline continues to fire independently — PostHog operates at the
// DOM level via autocapture, no shared helpers.
import posthog from 'posthog-js';

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let _initialised = null;

export function initPosthog() {
  if (_initialised !== null) return _initialised; // memoised — only init once

  if (!KEY) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info(
        '[posthog] REACT_APP_POSTHOG_KEY not set — analytics disabled. ' +
        'See /app/frontend/src/analytics/posthogClient.js header for setup.'
      );
    }
    _initialised = null;
    return null;
  }

  // Skip on localhost AND any *.preview.emergentagent.com preview pod so
  // dev/preview activity doesn't pollute production analytics. PostHog
  // only fires on theailedger.ai. Override with
  // REACT_APP_POSTHOG_CAPTURE_LOCAL=true if you ever need to debug.
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    const isLocalhost = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(host);
    const isPreviewPod = /\.preview\.emergentagent\.com$/i.test(host);
    if ((isLocalhost || isPreviewPod) &&
        process.env.REACT_APP_POSTHOG_CAPTURE_LOCAL !== 'true') {
      // eslint-disable-next-line no-console
      console.info(
        `[posthog] non-production host "${host}" — capture disabled.`
      );
      _initialised = null;
      return null;
    }
  }

  posthog.init(KEY, {
    api_host: HOST,
    // Use the latest recommended defaults (SPA pageviews via History API,
    // person profiles always, etc.) — see PostHog JS SDK docs.
    defaults: '2026-01-30',
    autocapture: true,
    capture_pageview: 'history_change',
    disable_session_recording: false,
    person_profiles: 'always',
    // Quieter console in production
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.info('[posthog] initialised, distinct_id:', ph.get_distinct_id());
      }
    },
  });

  _initialised = posthog;
  return posthog;
}

// Helper used by AuthContext / login pages to stitch anonymous browsing
// to the identified user. Pass the user's email (or null on logout).
export function identifyPosthog(user) {
  if (!_initialised) return;
  if (user && user.email) {
    _initialised.identify(user.email, {
      email: user.email,
      name: user.name,
      role: user.role,
    });
  } else {
    _initialised.reset();
  }
}
